import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiRotateCw, FiArrowLeft, FiArrowUpRight } from 'react-icons/fi';
import { trackEvent } from '../../lib/analytics';

/**
 * Tarjeta del catálogo con FLIP a la ficha comercial, al estilo de la Flow Store
 * del CORE-Front (giro en Y con el dorso contrarrotado para que no salga espejo).
 *
 * Detalles que costaron un par de vueltas:
 *  - El botón vive EN EL FLUJO, debajo de la tarjeta: flotando en una esquina
 *    se comía los logos de las plataformas.
 *  - El giro se dispara al pasar el ratón por el botón, pero el "volver" escucha
 *    en la TARJETA entera: si escuchara en el botón, al girar la cara el puntero
 *    dejaría de estar sobre él y la tarjeta entraría en un parpadeo infinito.
 *  - La altura se mide de la cara visible: el dorso suele ser más alto que el
 *    frente y, con altura fija, el contenido se cortaba por abajo.
 */
const IntegrationFlipCard = ({ integration, category, categoryDescription }) => {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState(false);
  // Fijado con clic: así el dorso no se cierra al mover el ratón para leerlo.
  const [pinned, setPinned] = useState(false);
  const [height, setHeight] = useState(null);

  const frontRef = useRef(null);
  const backRef = useRef(null);

  const commercial = integration?.commercial || null;
  const hasCommercial = Boolean(commercial);

  const accentColor = integration?.chart_color || '#7C74FF';
  const description = integration?.description || '';
  const platforms = Array.isArray(integration?.platforms) ? integration.platforms : [];

  const detailState = { integration, category, categoryDescription };

  // La tarjeta crece o encoge hasta la cara que se está viendo.
  const measure = useCallback(() => {
    const el = flipped ? backRef.current : frontRef.current;
    if (el) setHeight(el.offsetHeight);
  }, [flipped]);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    if (frontRef.current) ro.observe(frontRef.current);
    if (backRef.current) ro.observe(backRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const openBack = () => {
    if (!hasCommercial || flipped) return;
    trackEvent('Engagement', 'Flip Commercial Profile', integration?.title || 'Integration');
    setFlipped(true);
  };

  const closeBack = () => {
    if (pinned) return;
    setFlipped(false);
  };

  const togglePinned = () => {
    setPinned((prev) => {
      const next = !prev;
      setFlipped(next ? true : false);
      return next;
    });
  };

  const rows = hasCommercial
    ? [
        { key: 'pvp', label: t('commercial_pvp'), value: commercial.pvp_label },
        { key: 'value', label: t('commercial_value'), value: commercial.value_label },
        { key: 'time_saved', label: t('commercial_time_saved'), value: commercial.time_saved_label },
        { key: 'runtime', label: t('commercial_runtime'), value: commercial.runtime_label },
        { key: 'tokens', label: t('commercial_tokens'), value: commercial.tokens_label },
      ].filter((r) => r.value)
    : [];

  const observed = hasCommercial
    ? [
        { key: 'obs_tokens', label: t('commercial_observed_tokens'), value: commercial.observed_tokens_label },
        { key: 'obs_runtime', label: t('commercial_observed_runtime'), value: commercial.observed_runtime_label },
      ].filter((r) => r.value)
    : [];

  return (
    <div
      className={`integration-flip${flipped ? ' is-flipped' : ''}`}
      onMouseLeave={closeBack}
    >
      <div
        className="integration-flip__inner"
        style={height ? { height: `${height}px` } : undefined}
      >
        {/* ---------- Cara frontal ---------- */}
        <div className="integration-flip__face integration-flip__face--front" aria-hidden={flipped}>
          <div ref={frontRef}>
            <div
              className={`integration-card-new${integration?.maintenance ? ' is-maintenance' : ''}${
                integration?.agent ? ' is-agent' : ''
              }${hasCommercial ? ' has-flip' : ''}`}
              style={{ '--card-accent': accentColor }}
            >
              <Link
                to="/service-detail"
                state={detailState}
                className="integration-card-new__body"
                tabIndex={flipped ? -1 : undefined}
                onClick={() =>
                  trackEvent('Engagement', 'Open Integration', integration?.title || 'Integration')
                }
              >
                <div className="integration-card-new__header">
                  <div className="integration-card-new__title">
                    {integration?.title || t('integration')}
                  </div>
                  <div className="integration-card-new__meta">
                    {integration?.agent ? (
                      <span className="integration-badge integration-badge--agent">
                        {t('agentic')}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="integration-card-new__desc">{description}</div>
                <div className="integration-card-new__footer">
                  <div className="integration-card-new__chips">
                    <span className="integration-chip">{integration?.difficulty || 'Medium'}</span>
                    {integration?.pay_per_use ? (
                      <span className="integration-chip integration-chip--pay">
                        {t('pay_per_use')}
                      </span>
                    ) : null}
                  </div>
                  <div className="integration-card-new__platforms">
                    {platforms.slice(0, 5).map((platform, platformIndex) => {
                      const label = platform?.title || t('platform');
                      return platform?.logo ? (
                        <img
                          key={`platform-${platformIndex}`}
                          src={platform.logo}
                          alt={label}
                          className="integration-platform-logo"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div
                          key={`platform-${platformIndex}`}
                          className="integration-platform-placeholder"
                          title={label}
                        >
                          {label.charAt(0)}
                        </div>
                      );
                    })}
                    {platforms.length > 5 ? (
                      <div className="integration-platform-more">+{platforms.length - 5}</div>
                    ) : null}
                  </div>
                </div>
              </Link>

              {/* En el flujo, bajo la tarjeta: ya no tapa los logos. Basta con
                  pasar el ratón por encima para ver el dorso. */}
              {hasCommercial ? (
                <button
                  type="button"
                  className="integration-flip__toggle"
                  onMouseEnter={openBack}
                  onFocus={openBack}
                  onClick={togglePinned}
                  tabIndex={flipped ? -1 : undefined}
                  aria-label={t('commercial_flip_open')}
                >
                  <FiRotateCw size={14} />
                  <span>{t('commercial_flip_open')}</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* ---------- Dorso: ficha comercial ---------- */}
        {hasCommercial ? (
          <div className="integration-flip__face integration-flip__face--back" aria-hidden={!flipped}>
            <div ref={backRef}>
              <div
                className="integration-card-new integration-commercial"
                style={{ '--card-accent': accentColor }}
              >
                <div className="integration-commercial__header">
                  <span className="integration-commercial__kicker">{t('commercial_title')}</span>
                  <h4 className="integration-commercial__name">
                    {integration?.title || t('integration')}
                  </h4>
                  {commercial.availability_label ? (
                    <span className="integration-commercial__availability">
                      {commercial.availability_label}
                    </span>
                  ) : null}
                </div>

                <dl className="integration-commercial__rows">
                  {rows.map((row) => (
                    <div className="integration-commercial__row" key={row.key}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>

                {observed.length ? (
                  <div className="integration-commercial__observed">
                    <span className="integration-commercial__observed-title">
                      {t('commercial_observed_title')}
                    </span>
                    {observed.map((row) => (
                      <span className="integration-commercial__observed-row" key={row.key}>
                        {row.label}: <strong>{row.value}</strong>
                      </span>
                    ))}
                  </div>
                ) : null}

                {commercial.legal_notes ? (
                  <p className="integration-commercial__legal">{commercial.legal_notes}</p>
                ) : null}

                {commercial.is_estimate ? (
                  <p className="integration-commercial__estimate">{t('commercial_estimate_note')}</p>
                ) : null}

                <div className="integration-commercial__actions">
                  <button
                    type="button"
                    className="integration-flip__back"
                    onClick={() => {
                      setPinned(false);
                      setFlipped(false);
                    }}
                    tabIndex={flipped ? undefined : -1}
                  >
                    <FiArrowLeft size={14} />
                    {t('commercial_flip_close')}
                  </button>
                  <Link
                    to="/service-detail"
                    state={detailState}
                    className="integration-commercial__detail"
                    tabIndex={flipped ? undefined : -1}
                    onClick={() =>
                      trackEvent('Engagement', 'Open Integration', integration?.title || 'Integration')
                    }
                  >
                    {t('commercial_see_detail')}
                    <FiArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default IntegrationFlipCard;
